import { useInternalNode, getStraightPath } from "@xyflow/react";
import PropTypes from "prop-types";

// Helper to get the center of a node
function getNodeCenter(node) {
  if (!node) return { x: 0, y: 0 };
  const { position } = node;
  const width = node.width || node.measured?.width || 100;
  const height = node.height || node.measured?.height || 100;

  return {
    x: position.x + width / 2,
    y: position.y + height / 2,
  };
}

// Helper to find intersection point of line (center1 -> center2) with node rectangle
function getIntersectionPoint(node, otherNodeCenter) {
  const width = node.width || node.measured?.width || 100; // Fallback to 100 if not measured
  const height = node.height || node.measured?.height || 100;
  const center = getNodeCenter(node);

  const w = width / 2;
  const h = height / 2;

  const dx = otherNodeCenter.x - center.x;
  const dy = otherNodeCenter.y - center.y;

  if (dx === 0 && dy === 0) return center;

  const slope = dy / dx;

  // Try intersecting with top/bottom
  let x, y;
  if (Math.abs(dy) * w > Math.abs(dx) * h) {
    // Intersects with top or bottom
    y = dy > 0 ? h : -h;
    x = y / slope;
  } else {
    // Intersects with left or right
    x = dx > 0 ? w : -w;
    y = x * slope;
  }

  return {
    x: center.x + x,
    y: center.y + y,
  };
}

/**
 * WidgetConnector – Floating dashed connector line between a SCADA node and
 * its detached floating visualization widget.
 *
 * Dynamically calculates the intersection points with node boundaries so the
 * line always points to the nearest side.
 */
const WidgetConnector = ({ id, source, target, style }) => {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);

  if (!sourceNode || !targetNode) {
    return null;
  }

  const sourceCenter = getNodeCenter(sourceNode);
  const targetCenter = getNodeCenter(targetNode);

  const sourcePoint = getIntersectionPoint(sourceNode, targetCenter);
  const targetPoint = getIntersectionPoint(targetNode, sourceCenter);

  const [edgePath] = getStraightPath({
    sourceX: sourcePoint.x,
    sourceY: sourcePoint.y,
    targetX: targetPoint.x,
    targetY: targetPoint.y,
  });

  return (
    <g className="widget-connector-edge">
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke="#94a3b8"
        strokeWidth="1.2"
        strokeDasharray="4 4"
        opacity="0.8"
        style={style}
      />
    </g>
  );
};

WidgetConnector.propTypes = {
  id: PropTypes.string.isRequired,
  source: PropTypes.string.isRequired,
  target: PropTypes.string.isRequired,
  style: PropTypes.object,
};

export default WidgetConnector;
